<template>
    <div class="bg-surface-50 dark:bg-surface-900 flex flex-col overflow-hidden h-full">
        <!-- Search Field -->
        <div class="p-4 flex items-center">
            <InputText
                v-model="searchTerm"
                placeholder="Search by Name or Email"
                class="w-96"
                @input="onSearch"
                clearable
            />
        </div>
        <!-- Members Table -->
        <DataTable
            :value="members"
            paginator
            :rows="5"
            :rowsPerPageOptions="[5, 10, 20, 50]"
            tableStyle="min-width: 50rem h "
            scrollable
            scrollHeight="flex"
            row-hover
        >
            <Column>
                <template #header>
                    <span class="font-semibold text-sm text-muted-color">Name</span>
                </template>
                <template #body="{ data }">
                    <div class="flex items-center gap-4">
                        <Avatar
                            :image="'https://www.clevelanddentalhc.com/wp-content/uploads/2018/03/sample-avatar-768x768.jpg'"
                            class="mr-2"
                            size="large"
                            shape="circle"
                        />
                        <div>
                            <p class="mt-0 mb-2 font-medium text-lg text-color-primary">
                                {{ data.name }}
                            </p>
                            <p class="mt-0 mb-2 font-normal text-base text-muted-color">
                                {{ data.username }}
                            </p>
                        </div>
                    </div>
                </template>
            </Column>
            <Column>
                <template #header>
                    <span class="font-semibold text-sm text-muted-color">Joined Date</span>
                </template>
                <template #body="{ data }">
                    <p class="mt-0 mb-2 font-normal text-base text-muted-color">
                        {{ data.date }}
                    </p>
                </template>
            </Column>

            <Column class="min-w-[13rem]">
                <template #header>
                    <span class="font-semibold text-sm text-muted-color">Permissions</span>
                </template>
                <template #body="{ data }">
                    <div class="flex flex-col gap-2">
                        <!-- Permissions Tags -->
                        <div class="flex flex-wrap gap-2">
                            <Tag
                                v-for="(permission, index) in data.permissions.split(', ')"
                                :key="index"
                                :value="getPermissionsLabel(permission)"
                                :icon="getPermissionsIcon(permission)"
                                :severity="getPermissionsSeverity(permission)"
                                v-tooltip.top="getPermissionsDescription(permission)"
                            />
                        </div>
                    </div>
                </template>
            </Column>
            <Column class="min-w-[13rem]">
                <template #header>
                    <span class="font-semibold text-sm text-muted-color">Actions</span>
                </template>
                <template #body="{ data }">
                    <!-- Edit Permissions Button -->
                    <div class="flex items-center justify-start mt-2 gap-2">
                        <Button
                            v-if="authService.hasPermission(PermissionsEnum.editUserPermissions)"
                            v-tooltip="'Edit Permissions'"
                            icon="pi pi-lock-open"
                            severity="contrast"
                            class="p-button-secondary p-button-sm hover:bg-primary hover:text-white transition-all duration-300"
                            @click="openPermissionsDialog(data)"
                        />

                        <ToggleSwitch
                            v-if="authService.hasPermission(PermissionsEnum.editUser)"
                            v-model="data.status"
                            :true-value="'active'"
                            :false-value="'inactive'"
                            v-tooltip.top="`Deactivated users cannot log in`"
                            @update:model-value="updateMemberStatus(data)"
                        />
                    </div>
                </template>
            </Column>
        </DataTable>

        <!-- New PermissionsDialog component -->
        <PermissionsDialog
            v-model:visible="permissionsDialogVisible"
            :member="selectedMember"
            :groupedPermissions="groupedPermissions"
            :getPermissionsLabel="getPermissionsLabel"
            :getPermissionsDescription="getPermissionsDescription"
            :getPermissionsSeverity="getPermissionsSeverity"
            :getPermissionsIcon="getPermissionsIcon"
            @update:visible="permissionsDialogVisible = $event"
            @save="savePermissions"
            @toggle-permission="togglePermission"
        />
    </div>
</template>

<script setup>
import { ref } from "vue";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Avatar from "primevue/avatar";
import ToggleSwitch from "primevue/toggleswitch";
import Tag from "primevue/tag";
import { defineProps, onMounted, inject, reactive } from "vue";
import PermissionsDialog from "./PermissionsDialog.vue";
import InputText from "primevue/inputtext";
import UserRepository from "@/core/repositories/UserRepository";
import PermissionsRepository from "@/core/repositories/PermissionsRepository";
import UserModel from "@/core/models/UserModel";
import { PermissionsEnum } from "@/core/enums/PermissionsEnum";

const members = ref([]);
const permissionsDialogVisible = ref(false);
const selectedMember = ref(null);
const allPermissions = ref([]);
const groupedPermissions = ref({});
const permissionMapping = ref({});
const searchTerm = ref("");

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
});

const authService = inject("authService");

const fetchMembers = async (search = "") => {
    try {
        const response = await UserRepository.paginatedSearch(search);
        members.value = response.map((user) => new UserModel(user));
    } catch (error) {
        console.error("Error fetching members:", error);
    }
};

const updateMemberStatus = async (member) => {
    try {
        await UserRepository.updateStatus(member.id, member.status);
    } catch (error) {
        console.error("Error updating member status:", error);
    }
};

const openPermissionsDialog = (member) => {
    // Clone the member object to avoid direct mutation
    selectedMember.value = {
        ...member,
        permissions: member.permissions.split(", ").map((perm) => perm.trim()),
    };

    permissionsDialogVisible.value = true;
};

const savePermissions = async () => {
    try {
        await UserRepository.updatePermissions(selectedMember.value.id, selectedMember.value.permissions);

        const memberIndex = members.value.findIndex((m) => m.id === selectedMember.value.id);
        if (memberIndex !== -1) {
            members.value[memberIndex].permissions = selectedMember.value.permissions
                .map((permission) => getPermissionsLabel(permission))
                .join(", ");
        }

        // Re-fetch members to ensure the UI is in sync with the server after changes
        await fetchMembers();

        permissionsDialogVisible.value = false;
    } catch (error) {
        console.error("Error saving permissions:", error);
        alert("Failed to save permissions. Please try again.");
    }
};

const getPermissionsLabel = (permission) => {
    return permissionMapping.value?.[permission]?.label || "Unknown Permission";
};

const getPermissionsDescription = (permission) => {
    return permissionMapping.value?.[permission]?.description || "No description available.";
};
const getPermissionsSeverity = (permission) => {
    return permissionMapping.value?.[permission]?.severity || "secondary";
};
const getPermissionsIcon = (permission) => {
    return permissionMapping.value?.[permission]?.icon || "pi pi-question";
};

const fetchAllPermissions = async () => {
    try {
        const { permissions, mapping } = await PermissionsRepository.getAllPermissions();

        allPermissions.value = Object.values(permissions).flatMap((category) => Object.values(category));

        groupedPermissions.value = Object.entries(permissions).reduce((acc, [key, value]) => {
            const label = key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())
                .trim();
            acc[label] = Object.values(value);
            return acc;
        }, {});

        permissionMapping.value = mapping;
    } catch (error) {
        console.error("Error fetching permissions:", error);
    }
};

const togglePermission = (permissionName) => {
    if (selectedMember.value.permissions.includes(permissionName)) {
        selectedMember.value.permissions = selectedMember.value.permissions.filter((perm) => perm !== permissionName);
    } else {
        selectedMember.value.permissions.push(permissionName);
    }
};

const onSearch = () => {
    fetchMembers(searchTerm.value);
};

onMounted(async () => {
    await fetchMembers();
    await fetchAllPermissions();
});
</script>
